import type { DeepReadonly } from 'ts-essentials'
import type { ArrayElement, ISO8601Date } from './common'
import type { UserModel } from '.'

export interface Email {
  id: number
  subject: string
  body: string
  recipient_query: RecipientQuery
  delivered_count: number
  opened_count: number
  clicked_count: number
  owner_id: number
  creation_date: Date
  sent_date: Date | null
  status: number
  type: EmailType
  cached_to_id: number | null
}

export const RecipientType = ['test', 'superusers', 'admins', 'teachers', 'users', 'trialended'] as const
export type RecipientType = ArrayElement<typeof RecipientType>

export const EmailType = ['marketing', 'system'] as const
export type EmailType = ArrayElement<typeof EmailType>

export interface EmailEvent {
  id: number
  email_address: string
  event: string
  tag: string | null
  email_id: number | null
  user_id: number | null
  timestamp: Date
}

export interface EmailCount {
  id: number
  delivered_count: number
  opened_count: number
  clicked_count: number
}

export interface MailgunStats {
  accepted: number
  delivered: number
  opened: number
  clicked: number
  unsubscribed: number
}

export interface EmailHeader {
  'h:sender'?: string
  'h:X-Mailgun-Variables'?: MailGunVariables
  'h:X-Mailgun-Track-Clicks'?: 'yes' | 'no'
  'h:X-Mailgun-Track-Opens'?: 'yes' | 'no'
}

// these variables are passed back to our system when an event is logged
export interface MailGunVariables {
  user_id?: string // string so that recipient.user_id can be specified
  email_id?: string // string so that recipient.email_id can be specified
  tag?: string
  log?: boolean // determines whether our API logs the events from this email
}

// an array of Recipient Variables indexed to each user's email provides customisation within the email
export interface RecipientVariables {
  user_id?: number
  email_id?: number
  name?: string
  encodedEmail?: string
  verifyEmail?: string
  footer: string
  from: string
  fromName?: string
}

export interface SendableEmail {
  'to': string | string[]
  'from': string
  'subject': string
  'html': string
  'recipient-variables': string
  'h:X-Mailgun-Variables'?: string
  'h:X-Mailgun-Track-Clicks'?: 'yes' | 'no'
  'h:X-Mailgun-Track-Opens'?: 'yes' | 'no'
}

export interface EmailRecipient {
  id: number
  name: string
  email: string
  locale: string
  school_id: number | null
  school_name: string | null
}

export interface RecipientList {
  [index: string]: RecipientVariables
}

export interface RecipientQuery {
  roles: readonly EmailerRoleOptions[]
  actives: readonly EmailerSubActiveOptions[]
  types: readonly EmailerSubTypeOptions[]
  groups: readonly EmailerSubGroupOptions[]
  wonde: readonly EmailerWondeStatusOptions[]
  countries: readonly string[]
  price_types: readonly EmailerPriceTypeOptions[]
  ignore_marketing: boolean
}

export const EmailerRoleOptions = ['me', 'users', 'admins', 'teachers', 'superusers'] as const
export type EmailerRoleOptions = ArrayElement<typeof EmailerRoleOptions>

export const EmailerSubActiveOptions = ['active', 'expired'] as const
export type EmailerSubActiveOptions = ArrayElement<typeof EmailerSubActiveOptions>

export const EmailerSubTypeOptions = ['free', 'premium', 'none'] as const
export type EmailerSubTypeOptions = ArrayElement<typeof EmailerSubTypeOptions>

export const EmailerSubGroupOptions = ['edshed', 'litshed'] as const
export type EmailerSubGroupOptions = ArrayElement<typeof EmailerSubGroupOptions>

export const EmailerWondeStatusOptions = ['wondeLinked', 'wondeUnlinked'] as const
export type EmailerWondeStatusOptions = ArrayElement<typeof EmailerWondeStatusOptions>

export const EmailerPriceTypeOptions = ['fixed', 'metered'] as const
export type EmailerPriceTypeOptions = ArrayElement<typeof EmailerPriceTypeOptions>

export interface EmailSentResponse {
  success: boolean
  sent_count: number
}

export interface SendEmailJobParams {
  emailObj: Email
  user: DeepReadonly<UserModel>
}

export type MailGunLog = MailGunAcceptedLog | MailGunOpenedLog | MailGunDeliveredLog | MailGunClickedLog | MailGunComplainedLog | MailGunFailedLog

export interface MailGunLogResponse {
  start: Date | null
  end: Date | null
  items: MailGunLog[]
  pagination: {
    previous?: string
    next?: string
    first?: string
    last?: string
    total: number
  }
}

interface MailGunLogBase {
  id: string
  event: MailGunLogEvent
  '@timestamp': ISO8601Date
  account: {
    id: string
  }
  domain: {
    name: string
  }
  recipient: string
  'recipient-domain': string
  'log-level': string
  'user-variables': { [k in string]: any }
}

export interface MailGunAcceptedLog extends MailGunLogBase {
  event: 'accepted'
  tags: string[]
  method: string
  'originating-ip': string
  'api-key-id': string
  envelope: {
    sender: string
    transport: string
    targets: string
  }
  storage: {
    region: string
    env: string
    key: string
    url: string[]
  }
  message: {
    headers: {
      'message-id': string
    }
  }
  flags: {
    'is-authenticated': boolean
    'is-system-test': boolean
    'is-routed': boolean
    'is-test-mode': boolean
    'is-delayed-bounce': boolean
    'is-callback': boolean
  }
}

export interface MailGunOpenedLog extends MailGunLogBase {
  event: 'opened'
  tags: string[]
  'recipient-provider': string
  message: {
    headers: {
      'message-id': string
    }
  }
  ip: string
  geolocation: {
    city: string
    country: string
    region: string
    timezone: string
  }
  'client-info': {
    'client-name': string
    'client-os': string
    'client-type': string
    'device-type': string
    'user-agent': string
  }
}

export interface MailGunClickedLog extends MailGunLogBase {
  event: 'clicked'
  tags: string[]
  'recipient-provider': string
  message: {
    headers: {
      'message-id': string
    }
  }
  ip: string
  geolocation: {
    city: string
    country: string
    region: string
    timezone: string
  }
  'client-info': {
    'client-name': string
    'client-os': string
    'client-type': string
    'device-type': string
    'user-agent': string
  }
  url: string
}

export interface MailGunComplainedLog extends MailGunLogBase {
  event: 'complained'
  'recipient-provider': string
  envelope: {
    'sending-ip': string
  }
  storage: {
    region: string
    env: string
    key: string
    url: string[]
  }
  message: {
    headers: {
      to: string
      'message-id': string
      from: string
      subject: string
    }
    size: number
  }
}

export interface MailGunDeliveredLog extends MailGunLogBase {
  event: 'delivered'
  'delivery-status': {
    message: string
    'attempt-no': number
    code: number
    'session-seconds': number
    'mx-host': string
    'certificate-verified': boolean
    tls: boolean
    utf8: boolean
    'first-delivery-attempt-seconds': number
  }
  'recipient-provider': string
  envelope: {
    sender: string
    transport: string
    'sending-ip': string
    targets: string
  }
  storage: {
    region: string
    env: string
    key: string
    url: string[]
  }
  message: {
    headers: {
      to: string
      'message-id': string
      from: string
      subject: string
    }
    size: number
  }
  flags: {
    'is-authenticated': boolean
    'is-system-test': boolean
    'is-routed': boolean
    'is-test-mode': boolean
    'is-delayed-bounce': boolean
    'is-callback': boolean
  }
  'primary-dkim': string
}

export interface MailGunFailedLog extends MailGunLogBase {
  event: 'failed'
  'delivery-status': {
    message: string
    'attempt-no': number
    code: number
    'session-seconds': number
    'first-delivery-attempt-seconds': number
    'bounce-type': string
  }
  reason: string
  severity: string
  'recipient-provider': string
  envelope: {
    sender: string
    transport: string
    'sending-ip': string
    targets: string
  }
  storage: {
    region: string
    env: string
    key: string
    url: string[]
  }
  message: {
    headers: {
      to: string
      'message-id': string
      from: string
      subject: string
    }
    size: number
  }
  flags: {
    'is-authenticated': boolean
    'is-system-test': boolean
    'is-routed': boolean
    'is-test-mode': boolean
    'is-delayed-bounce': boolean
    'is-callback': boolean
  }
}

export const MailGunLogEvent = ['opened', 'accepted', 'delivered', 'clicked', 'failed', 'complained'] as const
export type MailGunLogEvent = typeof MailGunLogEvent[number]

export interface GetMailGunLogsParams {
  start?: Date
  end?: Date
  email?: string[]
  tag?: string[]
  subject?: string[]
  domain?: string[]
  messageId?: string
  events?: MailGunLogEvent[]
  pagination: {
    sort: string
    token?: string
    limit: number
  }
}
